import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, MoveDown } from '@factorialco/f0-react/icons/app'
import { TimeoffPolicyEmployeesCollectionListDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { convertDate } from 'modules/core/lib/dates'
import { useGraphQLClient } from 'modules/core/lib/graphql/provider'
import i18n from 'modules/core/lib/i18n'

import { useEmployeeFilters } from 'modules/employees/lib/filters'
import { hasPolicyTemporality } from 'modules/timeoff/lib/features/TimeoffFeatures'

interface Props {
  timeoffPolicyId: string
}

const TimeoffPolicyEmployeeList = ({
  timeoffPolicyId,
  children,
}: React.PropsWithChildren<Props>) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const client = useGraphQLClient()
  const {
    company,
    employee: { id: currentEmployeeId },
  } = useCurrentSession()
  const hasPolicyTemporalityEnabled = hasPolicyTemporality()

  const { filters: employeeFilters } = useEmployeeFilters()

  if (!client) {
    throw new Error('GraphQL client not found')
  }

  const source = useGraphqlDataCollectionSource(
    {
      document: TimeoffPolicyEmployeesCollectionListDocument,
      mapVariables: ({ filters, sortings, search }) => {
        const teamIds = filters.team?.map((id) => id)
        const locationIds = filters.workplace?.map((id) => id)
        const onlyMyReportees =
          filters.directReports &&
          filters.directReports.length > 0 &&
          (filters.directReports.includes('managed_by_me') ||
            filters.directReports.includes('managed_by_others'))
            ? filters.directReports?.includes('managed_by_me')
            : undefined
        const directReports =
          filters.directReports &&
          filters.directReports.length > 0 &&
          filters.directReports.includes('managed_directly_by_me')
            ? true
            : undefined
        const managedTimeoffByMe =
          filters.directReports &&
          filters.directReports.length > 0 &&
          filters.directReports.includes('managed_timeoff_by_me')
            ? [currentEmployeeId ?? '0']
            : undefined

        return {
          companyId: company.id,
          onlyMyReportees,
          reportingTo: filters.reportingTo?.map((id) => id),
          onlyDirectReports: directReports,
          timeoffManagerIds: managedTimeoffByMe,
          sort: sortings?.[0],
          search,
          teamIds: teamIds?.length ? teamIds : undefined,
          locationIds: locationIds?.length ? locationIds : undefined,
          timeoffPolicyId,
          hasPolicyTemporalityEnabled,
        }
      },
      mapPagination: (data) => {
        return {
          total: data.employees.employeesConnection.totalCount ?? 0,
        }
      },
      mapData: (data) => data.employees.employeesConnection.nodes,
      itemUrl: (item) => navigation.employees.id.timeOff.fullPath({ id: item.id }),
      sortings: {
        firstName: {
          label: i18n.t('employee_form.first_name'),
        },
        lastName: {
          label: i18n.t('employee_form.last_name'),
        },
      },
      defaultSortings: {
        field: 'firstName',
        order: 'asc',
      },
      search: {
        enabled: true,
      },
      filters: {
        team: employeeFilters.team,
        workplace: employeeFilters.workplace,
        directReports: {
          type: 'in',
          label: i18n.t('time_off.settings.employees.filters.managed_by_me'),
          options: {
            options: [
              {
                label: i18n.t('time_off.settings.employees.filters.managed_by_me_option'),
                value: 'managed_by_me',
              },
              {
                label: i18n.t('time_off.settings.employees.filters.managed_by_others'),
                value: 'managed_by_others',
              },
              {
                label: i18n.t('time_off.settings.employees.filters.managed_directly_by_me_option'),
                value: 'managed_directly_by_me',
              },
              {
                label: i18n.t('time_off.settings.employees.filters.managed_timeoff_by_me_option'),
                value: 'managed_timeoff_by_me',
              },
            ] as const,
          },
        },
        reportingTo: employeeFilters.reportingTo,
      },
      primaryActions: () => ({
        label: i18n.t('time_off.settings.employees.add.cta'),
        icon: Add,
        onClick: () =>
          navigate(
            navigation.settings.timeoffPolicies.id.employees.add.fullPath({
              id: timeoffPolicyId,
            })
          ),
      }),
      itemActions: (item) => [
        {
          label: i18n.t('settings.timeoff_policies.employees.actions.assign'),
          icon: MoveDown,
          enabled: !hasPolicyTemporalityEnabled,
          onClick: () => {
            navigate(
              navigation.settings.timeoffPolicies.id.employees.moveEmployeeId.fullPath({
                id: timeoffPolicyId,
                employeeId: item.id,
              })
            )
          },
        },
      ],
    },
    []
  )

  return (
    <>
      <Box paddingTop="s24">
        <OneDataCollection
          source={source}
          visualizations={[
            {
              type: 'table',
              options: {
                columns: [
                  {
                    label: i18n.t('time_off.settings.employees.first_name'),
                    sorting: 'firstName',
                    render: (item) => ({
                      type: 'person',
                      value: {
                        firstName: item.firstName,
                        lastName: '',
                        avatar: item.avatar?.url,
                      },
                    }),
                  },
                  {
                    label: i18n.t('time_off.settings.employees.last_name'),
                    sorting: 'lastName',
                    render: (item) => item.lastName,
                  },
                  {
                    label: '',
                    hidden: !hasPolicyTemporalityEnabled,
                    render: (item) => {
                      const effectiveAt =
                        item.timeoffpolicyassignmentsConnection?.edges[0]?.node.effectiveAt

                      if (!effectiveAt) {
                        return i18n.t('settings.timeoff_policies.employees.effective_at_contract')
                      }

                      return i18n.t('settings.timeoff_policies.employees.effective_at_date', {
                        date: convertDate(effectiveAt, 'backend', 'ui'),
                      })
                    },
                  },
                ],
              },
            },
          ]}
        />
      </Box>
      {children}
    </>
  )
}

export default observer(TimeoffPolicyEmployeeList)
